import { HttpMapper } from './http-mapper';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { KYC_ATTEMPT_STATUS, KYC_STATUS, MCC_CODE} from './app-variables';
import { APP_CONFIG } from '../../environment';
import { LoginProvider } from './login-provider';
import { OnfidoProvider } from './onfido-provider';
import { RouteProvider } from '../../providers/route/route';
import { TranslateService } from '@ngx-translate/core';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';

declare let idnow: any;

@Injectable()
export class KYCProvider {
  private isSignicatEnabled: boolean;
  private attemptsLeft: number = 0;

  constructor(
    private http: HttpMapper,
    private loginProvider: LoginProvider,
    private onfidoProvider: OnfidoProvider,
    private routeProvider: RouteProvider,
    private translate: TranslateService
  ) {}

  //SERVER METHODS
  start(countryNameAbbrev: string, mobilephone: string, phonePrefix: string, kycProvider: string): Observable<any> {
    let url = `${APP_CONFIG.API_URL}api/KYC`;
    let data = {
      country: countryNameAbbrev,
      mobilephone: mobilephone,
      kycproviderid: kycProvider,
      phoneprefix: phonePrefix
    };
    return this.http.post(url, data);
  }

  sendResult(
    kycSession: string | number,
    kycProvider: number,
    result: string,
    status: number,
    country: number,
    kycID: string,
    resultMessage?: string
  ): Observable<any> {
    let url = `${APP_CONFIG.API_URL}api/KYC`;
    let data = {
      KYCSessionID: kycSession,
      KYCProviderID: kycProvider,
      ResultID: result,
      ResultMessagge: resultMessage,
      StatusID: status,
      CountryID: country,
      kycID: kycID
    };
    return this.http.put(url, data);
  }

  getPrefixNumber(mcc: string): string {
    return MCC_CODE[mcc];
  }
  
  async openKyc(trackInfo, analyticsLocation: string) {
    const kycStatus = await this.loginProvider.getKycStatus();
    const attempt = kycStatus.attempt;
    const kycResult = kycStatus.kycResult;
    const isFirstTime = kycStatus.isFirstTime;
    const canAccess = await this.onfidoProvider.canAccessByAttempt();

    if (isFirstTime || attempt == KYC_ATTEMPT_STATUS.VERIFIED) {
      if (!canAccess)
        this.routeProvider.replace(
          this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF
        );
      else {
        this.onfidoProvider.setOnfidoFlow(false, true);
        let params = {
          analyticsLocation,
          showBack: true,
          showHelp: false,
          onSuccess: () => {
            this.navigateToHomePage(true);
          }
        };
        if (this.isSignicatEnabled)
          this.routeProvider.push(
            this.routeProvider.pages.EkycMethodSelectPage,
            params
          );
        else
          this.routeProvider.push(
            this.routeProvider.pages.OnfidoInfoPage,
            params
          );
      }
    } else if (attempt == KYC_ATTEMPT_STATUS.IN_PROGRESS) {
      this.routeProvider.openModal(this.routeProvider.pages.OnfidoPendingPage);
    } else if (attempt == KYC_ATTEMPT_STATUS.FAIL && this.attemptsLeft > 0) {
      this.onfidoProvider.setOnfidoFlow(false, true);
      this.routeProvider.push(this.routeProvider.pages.OnfidoInfoPage, {
        analyticsLocation,
        showBack: true,
        showHelp: false,
        onSuccess: () =>{
          this.navigateToHomePage(true)
        }
      });
    } else {
      if (kycResult == KYC_STATUS.KYC_SUSPECTED) {
        this.onfidoProvider.openOnfidoFlow(false, analyticsLocation, true);
      } else {
        if (!canAccess) {
          this.routeProvider.replace(
            this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF
          ),
            {
              trackEvent: trackInfo.trackEvent,
              trackParams: trackInfo.trackParams,
            };
        } else
          this.routeProvider.replace(this.routeProvider.pages.InfoPage, {
            showHelp: true,
            showBack: true,
            img: "assets/imgs/Warning.svg",
            title: this.translate.instant(
              "page.sendVerifyPage.sendVerifyFailedHeader"
            ),
            msg: this.translate.instant(
              "page.sendVerifyPage.verifyFailedContent"
            ),
            primaryButtonText: this.translate.instant(
              "page.sendVerifyPage.tryAgainButton"
            ),
            primaryButtonAction: () => {
              if (!canAccess) {
                this.routeProvider.replace(
                  this.routeProvider.pages.OnfidoNotVerifiedPageNonSMF,
                  {
                    trackEvent: trackInfo.trackEvent,
                    trackParams: trackInfo.trackParams,
                  }
                );
              } else {
                this.onfidoProvider.setOnfidoFlow(false, true);
                const params = {
                  analyticsLocation: analyticsLocation,
                  showBack: true,
                  showHelp: false,
                  onSuccess: () =>{
                    this.navigateToHomePage(true);
                  }

                };
                if (this.isSignicatEnabled)
                  this.routeProvider.replace(
                    this.routeProvider.pages.EkycMethodSelectPage,
                    params
                  );
                else
                  this.routeProvider.replace(
                    this.routeProvider.pages.OnfidoInfoPage,
                    params
                  );
              }
            },
          });
      }
    }
  }

  navigateToHomePage(canShowSuccessNotification){
    this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage);
    if(canShowSuccessNotification){
      RmtSnackbarComponent.present(this.translate.instant("page.walletHomepage.walletUpgradeSuccessful"));
    }
  }

  async isVeriffUserKyced(firstRefresh: boolean = false): Promise<boolean> {
    // TODO: Optimisation: Add back the logic once BE ads expirationDate to the KYC object in the signin response
    // const forceReload = (isFromSignIn && HttpProvider.user.IdentityVerification.Status === KYC_ATTEMPT_STATUS.VERIFIED && HttpProvider.user.IdentityVerification.ExpirationDate) ? false : true;
    const userIdentityVerification = await this.loginProvider.getIdentityVerificationAsync(firstRefresh);
    return userIdentityVerification ? userIdentityVerification.Status === KYC_ATTEMPT_STATUS.VERIFIED : false;
  }

  isWalletManualVerified() {
    const identifications = this.loginProvider.getUserCustomerIdentification()
    return identifications ? identifications.Verified === true : false
  }

  isManualVerificationExpired() {
    const identifications = this.loginProvider.getUserCustomerIdentification()
    return identifications ? identifications.VerificationExpired === true : false
  }
}



// WEBPACK FOOTER //
// ./src/providers/myria/kyc-provider.ts